import { c as _c } from "react/compiler-runtime";
import { Editor } from '@monaco-editor/react';
import { postOrgGraphsByGraphIdNodesByNodeIdValidateRules } from '@onefootprint/fetch-dashboard';
import { IcoTrash16 } from '@onefootprint/icons';
import type { PublicTraceEntry } from '@onefootprint/request-types/dashboard';
import { IconButton, LoadingSpinner, Shimmer } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import type { editor } from 'monaco-editor';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useStore from '@/playbook-builder/hooks/use-store';
import type { FormData } from '../../conditional-form';
import { useMonaco } from '../../hooks/use-monaco/use-monaco-context';
import { editorClassName, editorOptions } from '../../hooks/use-monaco/utils/monaco-themes/monaco-themes';
import CustomBadge from '../custom-badge';
import ValidationStatus from './components/validation-status';
import PendingIndicators from './components/validation-status/components/pending-indicators';
const LINE_HEIGHT = 24;
type RuleProps = {
  branchIndex: number;
  ruleIndex: number;
  onRemove: () => void;
  onReAdd: () => void;
  isPendingDeletion?: boolean;
  trace?: PublicTraceEntry;
  languageName: string;
  nodeId: string;
};
const Rule = t0 => {
  const $ = _c(90);
  const {
    branchIndex,
    ruleIndex,
    onRemove,
    onReAdd,
    isPendingDeletion,
    trace,
    languageName,
    nodeId
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    data: t1
  } = useStore();
  const {
    graph,
    canEdit
  } = t1;
  const {
    isLoading: isMonacoLoading,
    monaco
  } = useMonaco();
  const [isFocused, setIsFocused] = useState(false);
  const [heightPx, setHeightPx] = useState(LINE_HEIGHT);
  const theme = useTheme();
  const {
    control,
    setError,
    clearErrors,
    watch,
    formState: t2
  } = useFormContext();
  const {
    errors: formErrors
  } = t2;
  const fieldName = `branches.${branchIndex}.rules.${ruleIndex}.ruleExpression` as const;
  const t3 = `branches.${branchIndex}.rules.${ruleIndex}.ruleId`;
  let t4;
  if ($[0] !== t3 || $[1] !== watch) {
    t4 = watch(t3);
    $[0] = t3;
    $[1] = watch;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const ruleId = t4;
  let t5;
  if ($[3] !== control || $[4] !== fieldName) {
    t5 = {
      control,
      name: fieldName
    };
    $[3] = control;
    $[4] = fieldName;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const ruleExpressionValue = useWatch(t5);
  const fieldError = formErrors?.branches?.[branchIndex]?.rules?.[ruleIndex]?.ruleExpression;
  const [lastValidatedValue, setLastValidatedValue] = useState(ruleExpressionValue || "");
  let t6;
  if ($[6] !== graph || $[7] !== nodeId) {
    t6 = currentValue => postOrgGraphsByGraphIdNodesByNodeIdValidateRules({
      path: {
        graphId: graph.id,
        nodeId
      },
      body: {
        rule: currentValue
      }
    });
    $[6] = graph;
    $[7] = nodeId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== clearErrors || $[10] !== fieldName || $[11] !== languageName || $[12] !== monaco || $[13] !== ruleExpressionValue || $[14] !== ruleId || $[15] !== setError || $[16] !== t) {
    t7 = data => {
      const model = monaco?.editor?.getModel(monaco.Uri.parse(ruleId));
      if (!monaco || !model) {
        return;
      }
      if (data.data.isValid) {
        clearErrors(fieldName);
        setLastValidatedValue(ruleExpressionValue || "");
        monaco.editor.setModelMarkers(model, languageName, []);
        return;
      }
      const text = model.getValue();
      const problems = data.data.problems;
      const markers = problems.map(problem => {
        const startPosition = model.getPositionAt(byteOffsetToCharOffset(text, problem.byteOffsetStart));
        const endPosition = model.getPositionAt(byteOffsetToCharOffset(text, problem.byteOffsetEnd));
        return {
          severity: {
            error: monaco.MarkerSeverity.Error,
            warning: monaco.MarkerSeverity.Warning,
            info: monaco.MarkerSeverity.Info,
            hint: monaco.MarkerSeverity.Hint
          }[problem.severity],
          message: problem.message,
          startLineNumber: startPosition.lineNumber,
          startColumn: startPosition.column,
          endLineNumber: endPosition.lineNumber,
          endColumn: endPosition.column,
          code: problem.code ? {
            value: problem.code.value,
            target: monaco.Uri.parse(problem.code.uri)
          } : undefined
        };
      });
      monaco.editor.setModelMarkers(model, languageName, markers);
      setError(fieldName, {
        type: "manual",
        message: t(_temp)
      });
      setLastValidatedValue(ruleExpressionValue || "");
    };
    $[9] = clearErrors;
    $[10] = fieldName;
    $[11] = languageName;
    $[12] = monaco;
    $[13] = ruleExpressionValue;
    $[14] = ruleId;
    $[15] = setError;
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7
    };
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const validateRule = useMutation(t8);
  const isRuleExpressionChanged = ruleExpressionValue !== lastValidatedValue;
  let t9;
  if ($[21] !== isMonacoLoading || $[22] !== isPendingDeletion || $[23] !== isRuleExpressionChanged || $[24] !== nodeId || $[25] !== ruleExpressionValue || $[26] !== validateRule) {
    t9 = () => {
      if (!isMonacoLoading && isRuleExpressionChanged && nodeId && !isPendingDeletion) {
        const debounceTimeout = setTimeout(() => {
          validateRule.mutate(ruleExpressionValue);
        }, 1000);
        return () => clearTimeout(debounceTimeout);
      }
    };
    $[21] = isMonacoLoading;
    $[22] = isPendingDeletion;
    $[23] = isRuleExpressionChanged;
    $[24] = nodeId;
    $[25] = ruleExpressionValue;
    $[26] = validateRule;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== isMonacoLoading || $[29] !== isRuleExpressionChanged || $[30] !== nodeId || $[31] !== validateRule) {
    t10 = [isMonacoLoading, nodeId, validateRule, isRuleExpressionChanged];
    $[28] = isMonacoLoading;
    $[29] = isRuleExpressionChanged;
    $[30] = nodeId;
    $[31] = validateRule;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  useEffect(t9, t10);
  let t11;
  if ($[33] !== canEdit) {
    t11 = editor => {
      if (!editor) {
        return;
      }
      const triggerIfCursorNotInToken = () => {
        const position = editor.getPosition();
        const model_0 = editor.getModel();
        if (!position || !model_0) {
          return;
        }
        const text_0 = model_0.getLineContent(position.lineNumber);
        const charBeforeCursor = text_0.charAt(position.column - 2);
        const charAtCursor = text_0.charAt(position.column - 1);
        if (charBeforeCursor === " " || charBeforeCursor === "," || charBeforeCursor === "(" || charAtCursor === "" || charAtCursor === " " || charAtCursor === "]" || charAtCursor === ")") {
          editor.trigger("api", "editor.action.triggerSuggest", {
            auto: true
          });
        }
      };
      editor.onDidFocusEditorWidget(triggerIfCursorNotInToken);
      editor.onDidChangeCursorPosition(e => {
        if (!(e.reason || e.source === "deleteLeft" || e.source === "mouse" || e.source === "snippet")) {
          return;
        }
        setTimeout(() => {
          triggerIfCursorNotInToken();
        }, 100);
      });
      editor.onDidFocusEditorText(() => {
        setIsFocused(canEdit);
      });
      editor.onDidBlurEditorText(() => {
        setIsFocused(false);
      });
      editor.setScrollTop(0);
      setHeightPx(editor.getContentHeight());
      editor.onDidChangeModelContent(() => {
        editor.setScrollTop(0);
        setHeightPx(editor.getContentHeight());
      });
      editor.onDidLayoutChange(() => {
        editor.setScrollTop(0);
        setHeightPx(editor.getContentHeight());
      });
    };
    $[33] = canEdit;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const handleEditorDidMount = t11;
  const editorTheme = theme.resolvedTheme === "dark" ? "risk-rules-theme-dark" : "risk-rules-theme-light";
  let t12;
  if ($[35] !== ruleId || $[36] !== trace?.context?.branchResults || $[37] !== trace?.context?.kind) {
    t12 = trace?.context?.kind === "conditional" && (trace?.context?.branchResults?.some(b => b.results.some(r => r.result && r.ruleId === ruleId)) ?? false);
    $[35] = ruleId;
    $[36] = trace?.context?.branchResults;
    $[37] = trace?.context?.kind;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const wasRuleExecuted = t12;
  const borderClass = wasRuleExecuted ? "border-accent" : "border-primary";
  const borderWidth = wasRuleExecuted ? "border-2" : "border";
  const isInitialRule = ruleIndex === 0;
  const t13 = isFocused ? "border-secondary shadow-focus-accent" : borderClass;
  let t14;
  if ($[39] !== borderWidth || $[40] !== t13) {
    t14 = cx(["flex-1 rounded relative w-full min-w-0 border-box", t13, borderWidth]);
    $[39] = borderWidth;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== isPendingDeletion) {
    t15 = isPendingDeletion && <div className="absolute inset-0 z-10 w-full h-full bg-secondary/50" />;
    $[42] = isPendingDeletion;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== isInitialRule || $[45] !== t) {
    t16 = isInitialRule ? t(_temp2) : t(_temp3);
    $[44] = isInitialRule;
    $[45] = t;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16) {
    t17 = <div className="flex items-center justify-center border-r border-tertiary px-1.5 min-w-[30px] bg-secondary rounded-l"><p className="flex-shrink-0 text-center text-caption-1 text-tertiary">{t16}</p></div>;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== canEdit || $[50] !== control || $[51] !== editorTheme || $[52] !== fieldName || $[53] !== handleEditorDidMount || $[54] !== heightPx || $[55] !== isMonacoLoading || $[56] !== isPendingDeletion || $[57] !== languageName || $[58] !== ruleId) {
    t18 = isMonacoLoading ? <LoadingSpinner /> : <div className="flex-1 min-w-0"><Controller name={fieldName} control={control} render={t19 => {
        const {
          field
        } = t19;
        return <Editor height={`${heightPx}px`} language={languageName} value={field.value ?? ""} loading={<Shimmer className="w-full h-full" />} theme={editorTheme} options={{
          ...editorOptions,
          readOnly: !canEdit || isPendingDeletion,
          lineHeight: LINE_HEIGHT
        }} onChange={canEdit ? field.onChange : undefined} onMount={handleEditorDidMount} className={`${editorClassName} ${canEdit ? "editable" : "not-editable"}`} defaultPath={ruleId} />;
      }} /></div>;
    $[49] = canEdit;
    $[50] = control;
    $[51] = editorTheme;
    $[52] = fieldName;
    $[53] = handleEditorDidMount;
    $[54] = heightPx;
    $[55] = isMonacoLoading;
    $[56] = isPendingDeletion;
    $[57] = languageName;
    $[58] = ruleId;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t || $[61] !== wasRuleExecuted) {
    t19 = wasRuleExecuted && <CustomBadge>{t(_temp4)}</CustomBadge>;
    $[60] = t;
    $[61] = wasRuleExecuted;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  const t20 = validateRule.isPending && isRuleExpressionChanged && !isPendingDeletion;
  let t21;
  if ($[63] !== fieldError || $[64] !== t20) {
    t21 = <ValidationStatus shouldShowLoading={t20} fieldError={fieldError} />;
    $[63] = fieldError;
    $[64] = t20;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== t18 || $[67] !== t19 || $[68] !== t21) {
    t22 = <div className="flex items-center flex-1 min-w-0 gap-2 px-3 py-1">{t18}{t19}{t21}</div>;
    $[66] = t18;
    $[67] = t19;
    $[68] = t21;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[70] !== t17 || $[71] !== t22) {
    t23 = <div className="flex items-stretch w-full min-w-0">{t17}{t22}</div>;
    $[70] = t17;
    $[71] = t22;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== t14 || $[74] !== t15 || $[75] !== t23) {
    t24 = <div className={t14}>{t15}{t23}</div>;
    $[73] = t14;
    $[74] = t15;
    $[75] = t23;
    $[76] = t24;
  } else {
    t24 = $[76];
  }
  let t25;
  if ($[77] !== isPendingDeletion || $[78] !== onReAdd) {
    t25 = isPendingDeletion && <PendingIndicators onReAdd={onReAdd} />;
    $[77] = isPendingDeletion;
    $[78] = onReAdd;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== canEdit || $[81] !== isPendingDeletion || $[82] !== onRemove || $[83] !== ruleIndex || $[84] !== t) {
    t26 = canEdit && !isPendingDeletion && <IconButton onClick={onRemove} className="shrink-0" size="tiny" icon={IcoTrash16} destructive={true} aria-label={t(_temp5, {
      index: ruleIndex + 1
    })} />;
    $[80] = canEdit;
    $[81] = isPendingDeletion;
    $[82] = onRemove;
    $[83] = ruleIndex;
    $[84] = t;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  let t27;
  if ($[86] !== t24 || $[87] !== t25 || $[88] !== t26) {
    t27 = <li className="flex flex-col w-full"><div className="grid grid-cols-[1fr_auto] gap-2 items-center justify-items-start max-w-full">{t24}{t25}{t26}</div></li>;
    $[86] = t24;
    $[87] = t25;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  return t27;
};

// Converts form byte offsets (from the backend) and character offsets (used by Monaco).
function byteOffsetToCharOffset(text: string, byteOffset: number): number {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(text);
  const truncatedBytes = bytes.slice(0, byteOffset);
  const decoder = new TextDecoder();
  return decoder.decode(truncatedBytes).length;
}
export default Rule;
function _temp($) {
  return $.rulesNode.rule.validationError;
}
function _temp2($_0) {
  return $_0.rulesNode.rule.ifLabel;
}
function _temp3($_1) {
  return $_1.rulesNode.rule.orLabel;
}
function _temp4($_2) {
  return $_2.rules.triggered;
}
function _temp5($_3) {
  return $_3.rulesNode.rule.removeAriaLabel;
}